import type { TeamTypesEnum } from 'types/generated/client';
import { GAME_FORMAT_LABELS, GameFormat, getGameFormat } from 'components/Find/gameFormat';

/**
 * The structural shape a title needs from a listing's rating scale row. Kept as a
 * loose interface (not a query-specific generated type) so both a game's
 * `event_group` scales and a player availability's scales satisfy it.
 */
interface RatingScaleLike {
  minimumRating?: number | null;
  maximumRating?: number | null;
  pickleballRatingScale?: { shortName: string } | null;
  tennisRatingScale?: { shortName: string } | null;
  padelRatingScale?: { shortName: string } | null;
}

interface SkillLevelLike {
  displayName: string;
}

/** The structural shape a title needs from a game group / availability posting. */
export interface GameTitleSource {
  /** Null = Open Play, which gets no format prefix in the title. */
  teamType?: TeamTypesEnum | null;
  /** A game's single self-rated level. */
  selfRatedSkillLevel?: SkillLevelLike | null;
  /** An availability's self-rated level range (min = max for one level). */
  selfRatedMinSkillLevel?: SkillLevelLike | null;
  selfRatedMaxSkillLevel?: SkillLevelLike | null;
  ratingScales: RatingScaleLike[];
}

/** "Advanced" or "Intermediate - Advanced", from whichever level fields the source carries. */
export const formatSelfRatedLevel = (source: GameTitleSource): string | undefined => {
  const min = source.selfRatedMinSkillLevel?.displayName ?? source.selfRatedSkillLevel?.displayName;
  const max = source.selfRatedMaxSkillLevel?.displayName;
  if (!min) return undefined;
  return max && max !== min ? `${min} - ${max}` : min;
};

/** The scale's short name for whichever sport FK the join row carries. */
const ratingScaleShortName = (scale: RatingScaleLike): string =>
  scale.pickleballRatingScale?.shortName ??
  scale.tennisRatingScale?.shortName ??
  scale.padelRatingScale?.shortName ??
  '';

/**
 * The composed listing title — e.g. "Doubles • DUPR 3 - 5 • Advanced". The match
 * type comes from the team type, and the rating half is rebuilt from the group's
 * scales + self-rated level (mirrors the list form's preview). Shared by the /find
 * games list and the host-side requests summary so both read identically.
 */
export const buildGameTitle = (source: GameTitleSource): string => {
  const format = getGameFormat(source.teamType);
  const matchTypeLabel = format === GameFormat.OpenPlay ? '' : GAME_FORMAT_LABELS[format];
  const ratingParts = source.ratingScales.map((scale) => {
    const shortName = ratingScaleShortName(scale);
    return scale.minimumRating != null && scale.maximumRating != null
      ? `${shortName} ${scale.minimumRating} - ${scale.maximumRating}`.trim()
      : shortName;
  });
  const level = formatSelfRatedLevel(source);
  if (level) ratingParts.push(level);
  const title = [matchTypeLabel, ...ratingParts].filter(Boolean).join(' • ');
  // An Open Play with no rating requirement would otherwise have no title at all.
  return title || GAME_FORMAT_LABELS[format];
};
